import { HttpApiService } from "@/shared/services/httpApi.service";

import { INTERNAL_IDENTITIES_URL } from "../constants/cardholderOnboarding.constants";
import type {
  CardholderBoardQuery,
  CardholderBoardResponse,
  CardholderOnboardingRun,
  VerificationDiscoveryRun,
  VerificationSyncRun,
} from "../types/cardholderOnboarding.types";

/** The staff endpoints behind `/app/cardholder-onboarding` — the only place that knows the wire. */
export const CardholderOnboardingService = {
  async board(query: CardholderBoardQuery): Promise<CardholderBoardResponse> {
    const { data } = await HttpApiService.get<CardholderBoardResponse>(
      `${INTERNAL_IDENTITIES_URL}/cardholder-onboarding`,
      { params: query },
    );
    return data;
  },

  async runOnboarding(payload: {
    organization_id: string;
    identity_ids: string[];
    dry_run: boolean;
  }): Promise<CardholderOnboardingRun> {
    if (payload.identity_ids.length === 0) {
      throw new Error(
        "runOnboarding needs at least one identity: an empty list scans the whole organization",
      );
    }
    // `limit` defaults to 25 server-side; the run has to cover every identity it was given.
    const { data } = await HttpApiService.post<CardholderOnboardingRun>(
      `${INTERNAL_IDENTITIES_URL}/cardholder-onboarding`,
      { ...payload, limit: payload.identity_ids.length },
    );
    return data;
  },

  async discoverVerification(payload: {
    identity_ids?: string[];
    applicant_ids?: string[];
    dry_run: boolean;
  }): Promise<VerificationDiscoveryRun> {
    const { data } = await HttpApiService.post<VerificationDiscoveryRun>(
      `${INTERNAL_IDENTITIES_URL}/sumsub-discovery`,
      payload,
    );
    return data;
  },

  async syncVerification(payload: {
    identity_ids: string[];
    dry_run: boolean;
    repoint_to_green?: boolean;
  }): Promise<VerificationSyncRun> {
    const { data } = await HttpApiService.post<VerificationSyncRun>(
      `${INTERNAL_IDENTITIES_URL}/sumsub-status-reconciliation`,
      payload,
    );
    return data;
  },

  async retryRegistration(identityId: string, organizationId: string): Promise<void> {
    await HttpApiService.post(
      `${INTERNAL_IDENTITIES_URL}/${identityId}/retry-provider`,
      {},
      { params: { organization_id: organizationId } },
    );
  },
};
